import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Card,
  CardContent,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Avatar,
  alpha,
  useTheme,
  Divider,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import {
  Person,
  Edit,
  Delete,
  Save,
  Cancel,
  SupervisorAccount,
  GroupAdd,
  Group,
  Work,
  CheckBoxOutlineBlank,
  CheckBox as CheckBoxIcon,
} from "@mui/icons-material";
import api from "../../api";
import DataTableFrame from "../../components/common/DataTableFrame";
import PageShell, { PanelCard } from "../../components/common/PageShell";

// Checkbox İkonları
const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// --- MODERN UI BİLEŞENLERİ (DEĞİŞMEDİ) ---
const ModernInputWrapper = ({ children }) => (
  <Box
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        backgroundColor: "#fff",
        transition: "all 0.2s ease-in-out",
        "& fieldset": { borderColor: "#e0e0e0" },
        "&:hover": {
          backgroundColor: "#fafafa",
          "& fieldset": { borderColor: "#bdbdbd" },
        },
        "&.Mui-focused": {
          backgroundColor: "#fff",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          "& fieldset": { borderWidth: "1px", borderColor: "primary.main" },
        },
      },
      "& .MuiInputLabel-root": { fontSize: "0.85rem" },
      "& .MuiInputBase-input": { fontSize: "0.9rem", padding: "10px 14px" },
    }}
  >
    {children}
  </Box>
);

const ModernCardHeader = ({ title, icon, color, count }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ pb: 2, mb: 3, borderBottom: "1px solid rgba(0,0,0,0.06)" }}
  >
    <Stack direction="row" alignItems="center" spacing={2}>
      <Avatar
        variant="rounded"
        sx={{ bgcolor: alpha(color, 0.1), color: color, width: 40, height: 40 }}
      >
        {icon}
      </Avatar>
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            color: "text.primary",
            textTransform: "uppercase",
            fontSize: "0.85rem",
            letterSpacing: 0.5,
          }}
        >
          {title}
        </Typography>
        {count !== undefined && (
          <Typography variant="caption" color="text.secondary">
            Toplam {count} Kayıt
          </Typography>
        )}
      </Box>
    </Stack>
  </Stack>
);

export default function UserManagementPage() {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);

  const [formData, setFormData] = useState({
    user: "",
    pass: "",
    name: "",
    role: "field_agent",
    coordinator_id: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // Verileri Çek
  const fetchUsers = async () => {
    try {
      const res = await api.get("/admin/users");
      if (res.data.success) setUsers(res.data.users);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCoordinators = async () => {
    try {
      const res = await api.get("/admin/coordinators");
      if (res.data.success) setCoordinators(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchJobs = async () => {
    try {
      const res = await api.get("/admin/jobs");
      if (res.data.success) setJobs(res.data.jobs);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCoordinators();
    fetchJobs();
  }, []);

  const handleSubmit = async () => {
    if (!formData.user) return alert("Kullanıcı adı zorunlu!");

    try {
      const payload = {
        username: formData.user,
        password: formData.pass,
        full_name: formData.name,
        role: formData.role,
        coordinator_id:
          formData.role === "field_agent" ? formData.coordinator_id : null,
        jobIds: selectedJobs.map((j) => j.id),
      };

      if (isEditing) {
        await api.put(`/admin/users/${editId}`, payload);
        await api.post("/admin/assign-jobs", {
          userId: editId,
          jobIds: payload.jobIds,
        });
        alert("Kullanıcı ve görevleri güncellendi.");
      } else {
        await api.post("/admin/create-user", payload);
        alert("Personel ve görevleri eklendi.");
      }

      resetForm();
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || "İşlem sırasında bir hata oluştu.");
    }
  };

  const handleEditClick = (u) => {
    setIsEditing(true);
    setEditId(u.id);
    setFormData({
      user: u.username,
      name: u.full_name,
      role: u.role,
      coordinator_id: u.coordinator_id || "",
      pass: "",
    });

    let parsedJobs = [];
    if (u.assigned_jobs) {
      if (typeof u.assigned_jobs === "string") {
        try {
          parsedJobs = JSON.parse(u.assigned_jobs);
        } catch (error) {
          console.error("İş atamaları parse edilemedi:", error);
          parsedJobs = [];
        }
      } else if (Array.isArray(u.assigned_jobs)) {
        parsedJobs = u.assigned_jobs;
      }
    }

    parsedJobs = parsedJobs.filter((job) => job !== null);
    setSelectedJobs(parsedJobs);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      fetchUsers();
    } catch (error) {
      alert("Silinemedi");
    }
  };

  const resetForm = () => {
    setFormData({
      user: "",
      pass: "",
      name: "",
      role: "field_agent",
      coordinator_id: "",
    });
    setSelectedJobs([]);
    setIsEditing(false);
    setEditId(null);
  };

  return (
    <PageShell title="Personel Yönetimi" icon={<Group />}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "350px 1fr" },
          gap: 4,
        }}
      >
      <Box>
        <Card
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: `1px solid ${isEditing ? theme.palette.warning.main : "#eaecf0"}`,
            position: "sticky",
            top: 84,
            boxShadow: isEditing
              ? `0 0 0 4px ${alpha(theme.palette.warning.main, 0.1)}`
              : "none",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <ModernCardHeader
              title={isEditing ? "Personeli Düzenle" : "Yeni Personel"}
              icon={isEditing ? <Edit /> : <GroupAdd />}
              color={
                isEditing
                  ? theme.palette.warning.main
                  : theme.palette.primary.main
              }
            />

            <Stack spacing={2.5}>
              <ModernInputWrapper>
                <TextField
                  label="Ad Soyad"
                  fullWidth
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
              </ModernInputWrapper>

              <ModernInputWrapper>
                <TextField
                  label="Kullanıcı Adı"
                  fullWidth
                  value={formData.user}
                  onChange={(e) =>
                    setFormData({ ...formData, user: e.target.value })
                  }
                />
              </ModernInputWrapper>

              <ModernInputWrapper>
                <FormControl fullWidth size="small">
                  <InputLabel>Kullanıcı Rolü</InputLabel>
                  <Select
                    value={formData.role}
                    label="Kullanıcı Rolü"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        role: e.target.value,
                        coordinator_id: "",
                      })
                    }
                  >
                    <MenuItem value="field_agent">Saha Ekibi</MenuItem>
                    <MenuItem value="project_coordinator">
                      Proje Koordinatörü
                    </MenuItem>
                    {/* YENİ EKLENEN ROL */}
                    <MenuItem value="team_leader">Ekip Lideri</MenuItem>
                    <MenuItem value="admin">Admin (Tam Yetki)</MenuItem>
                    <MenuItem value="auditor">
                      Kontrol Mühendisi (Sadece İnceleme)
                    </MenuItem>
                  </Select>
                </FormControl>
              </ModernInputWrapper>

              {/* ATANACAK ÇALIŞMALAR (MULTI-SELECT) */}
              <ModernInputWrapper>
                <Autocomplete
                  multiple
                  size="small"
                  options={jobs}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.job_name}
                  value={selectedJobs}
                  onChange={(event, newValue) => setSelectedJobs(newValue)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.job_name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Yetkili Olduğu Çalışmalar"
                      placeholder="İş Seçin..."
                    />
                  )}
                />
              </ModernInputWrapper>

              {formData.role === "field_agent" && (
                <ModernInputWrapper>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!formData.coordinator_id}
                  >
                    <InputLabel>Bağlı Olduğu Koordinatör</InputLabel>
                    <Select
                      value={formData.coordinator_id}
                      label="Bağlı Olduğu Koordinatör"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          coordinator_id: e.target.value,
                        })
                      }
                    >
                      {coordinators.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.full_name}
                        </MenuItem>
                      ))}
                    </Select>
                    {!formData.coordinator_id && (
                      <FormHelperText>Zorunlu alan</FormHelperText>
                    )}
                  </FormControl>
                </ModernInputWrapper>
              )}

              <ModernInputWrapper>
                <TextField
                  label={isEditing ? "Yeni Şifre (İsteğe Bağlı)" : "Şifre"}
                  type="password"
                  fullWidth
                  value={formData.pass}
                  onChange={(e) =>
                    setFormData({ ...formData, pass: e.target.value })
                  }
                />
              </ModernInputWrapper>

              <Stack direction="row" spacing={1} pt={1}>
                <Button
                  variant="contained"
                  fullWidth
                  color={isEditing ? "warning" : "primary"}
                  onClick={handleSubmit}
                  sx={{ borderRadius: "10px", py: 1.2, fontWeight: "bold" }}
                  startIcon={isEditing ? <Save /> : <Save />}
                >
                  {isEditing ? "Güncelle" : "Personeli Kaydet"}
                </Button>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={resetForm}
                    sx={{ borderRadius: "10px" }}
                  >
                    <Cancel />
                  </Button>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* SAĞ TARAF: TABLO KARTI */}
      <Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: "1px solid #eaecf0",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid #f0f0f0" }}>
            <ModernCardHeader
              title="Sistemdeki Personeller"
              icon={<Group />}
              color={theme.palette.secondary.main}
              count={users.length}
            />
          </Box>
          <DataTableFrame
            plain
            minWidth={900}
            maxHeight={null}
            stickyHeader={false}
          >
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Personel Bilgisi
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Rol</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Üst Yönetici</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    İşlemler
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow
                    key={u.id}
                    hover
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: "primary.main",
                            fontWeight: "bold",
                            fontSize: "0.85rem",
                          }}
                        >
                          {u.full_name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="800">
                            {u.full_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{u.username}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          u.role === "admin"
                            ? "Admin"
                            : u.role === "project_coordinator"
                              ? "Koordinatör"
                              : u.role === "team_leader"
                                ? "Ekip Lideri"
                                : u.role === "auditor"
                                  ? "Kontrol Mühendisi" // YENİ EKLENDİ
                                  : "Saha Ekibi"
                        }
                        size="small"
                        variant="filled"
                        color={
                          u.role === "admin"
                            ? "error"
                            : u.role === "project_coordinator"
                              ? "primary"
                              : u.role === "team_leader"
                                ? "success"
                                : u.role === "auditor"
                                  ? "secondary" // Denetçi Rengi
                                  : "default"
                        }
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                          borderRadius: "6px",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {u.role === "field_agent" ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SupervisorAccount
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography variant="caption" fontWeight="500">
                            {u.coordinator_name || "Atanmamış"}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(u)}
                        sx={{
                          color: "primary.main",
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          mr: 1,
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(u.id)}
                        sx={{
                          color: "error.main",
                          bgcolor: alpha(theme.palette.error.main, 0.05),
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
          </DataTableFrame>
        </Paper>
      </Box>
      </Box>
    </PageShell>
  );
}
